// War panel: status and intensity claims (as_of + sources), then three columns:
//
//   ┌ theatre ───────────┐ ┌ key events ─┐ ┌ statements by side ─────────┐
//   │ inset map at D     │ │ ◆ date      │ │ side A          side B      │
//   │ (owners, stripes)  │ │   text [📖] │ │ “quote” …       “quote” …   │
//   │ control over time  │ │ ◆ …         │ │                             │
//   │ ▁▂▃▅▆▇ share held  │ │             │ │                             │
//   └────────────────────┘ └─────────────┘ └─────────────────────────────┘
//
// The inset paints the snapshot's owners and occupation stripes around the theatre states;
// the chart plots world.controlSeries(war) when the services provide it.
//
// Everything shown is the war as it stood on the date shown (warViewOf): the curated status and
// intensity claims are dated near the as-of date, so an earlier date gets the dated status, no
// intensity until one is dated, and only the events, statements and end date already reached.
// Statements are read from the war, by party id: non-state parties (Hezbollah, Hamas, Houthis)
// have no country report.

import { useEffect, useRef } from 'preact/hooks';
import { COLS, HEX_COUNT, HEX_W, ROW_H, hexCol, hexRow } from '../core/grid.ts';
import type { SourceId, War } from '../core/schema.ts';
import type { WorldService } from '../services/api.ts';
import type { WorldSnapshot } from '../world/api.ts';
import { parseHex } from './color.ts';
import { StatementCard } from './CountryPanel.tsx';
import { useServices } from './context.ts';
import { controlHistory, warViewOf, type ControlHistory, type WarView } from './extensions.ts';
import { fmtDate, fmtShare } from './format.ts';
import { Icon } from './icons.tsx';
import { statementBase, uniqueKeys } from './keys.ts';
import { actorEmblem, actorName, lookOf, reportOf, selfIso } from './nation.ts';
import { AsOf, ClaimLabel, Medallion, Screen, SourceChip } from './parts.tsx';

const SIDE_MEDAL = 46;
const INSET_MARGIN = 6; // hexes of context around the theatre
const INSET_MAX_COLS = COLS / 2; // a theatre wider than half the world gets no inset
const INSET_PX_PER_COL = 2;
const INSET_DIM = 0.45; // non-theatre owners fade back
const STRIPE_EVERY = 3;
const CHART_W = 300;
const CHART_H = 110;
const CHART_HEADROOM = 1.15;
const EVENT_KEY_CHARS = 16;

const KIND_TEXT: Record<War['kind'], string> = {
  interstate: 'Interstate war',
  civil: 'Civil war',
  insurgency: 'Insurgency',
  intervention: 'Intervention',
  'border-clash': 'Border clash',
};

export function WarPanel() {
  const services = useServices();
  const { state, actions } = services;
  const id = state.panelArg.value;
  const view = id ? warViewOf(services.world, id) : null;
  const close = () => actions.openPanel('none');

  if (!view) {
    return (
      <Screen title="War" icon="war" onClose={close} class="wc-screen--narrow">
        <div class="wc-empty">This war is not in the world state at this date.</div>
      </Screen>
    );
  }

  const { war, status, intensity, events, ended } = view;
  const self = selfIso(services);
  const eventKeys = uniqueKeys(events.map((e) => e.date + e.text.slice(0, EVENT_KEY_CHARS)));
  const theatre = war.theatre.map((c) => actorName(services.world, c)).join(', ');

  return (
    <Screen
      title={war.name}
      icon="war"
      class="wc-war-screen"
      subtitle={
        <>
          {KIND_TEXT[war.kind]} · since {fmtDate(war.from)}
          {ended ? ` · until ${fmtDate(ended)}` : ''} · theatre: {theatre} <SourceChip ids={war.sources} />
        </>
      }
      onClose={close}
    >
      <div class="wc-war-sides">
        <SideBanner actors={war.sides.a} self={self} align="left" />
        <div class="wc-war-vs">
          <Icon name="war" size={34} />
          <span>vs</span>
        </div>
        <SideBanner actors={war.sides.b} self={self} align="right" />
      </div>

      <div class="wc-war-claims">
        <ClaimCard label="Status" claim={status} />
        {intensity ? <ClaimCard label="Intensity" claim={intensity} /> : <NoReading label="Intensity" />}
      </div>

      <div class="wc-war-body">
        <section class="wc-war-theatre wc-fade">
          <h3 class="wc-rule-title">Theatre</h3>
          <TheatreInset war={war} />
          <ControlChart war={war} />
        </section>

        <section class="wc-war-timeline">
          <h3 class="wc-rule-title">Key events</h3>
          <ol class="wc-timeline wc-scroll">
            {events.length === 0 ? <li class="wc-empty">No sourced events yet</li> : null}
            {events.map((e, i) => (
              <li key={eventKeys[i]}>
                <span class="wc-timeline-date">{fmtDate(e.date)}</span>
                <span class="wc-timeline-text">
                  {e.text} <SourceChip ids={e.sources} />
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section class="wc-war-other">
          <h3 class="wc-rule-title">Statements by side</h3>
          <div class="wc-war-columns">
            <StatementColumn view={view} actors={war.sides.a} />
            <StatementColumn view={view} actors={war.sides.b} />
          </div>
        </section>
      </div>
    </Screen>
  );
}

function SideBanner({ actors, self, align }: { actors: string[]; self: string | null; align: 'left' | 'right' }) {
  const { world } = useServices();
  return (
    <div class={`wc-war-side wc-war-side--${align}`}>
      {actors.map((a) => {
        return (
          <div class={a === self ? 'wc-war-party wc-war-party--self' : 'wc-war-party'} key={a}>
            <Medallion {...actorEmblem(world, a)} size={SIDE_MEDAL} />
            <span>{actorName(world, a)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ClaimCard({ label, claim }: { label: string; claim: WarView['status'] }) {
  return (
    <div class={`wc-claim-card wc-claim-card--${claim.value}`}>
      <div class="wc-claim-card-label">{label}</div>
      <div class="wc-claim-card-value">{claim.value}</div>
      <div class="wc-claim-card-meta">
        <AsOf date={claim.as_of} />
        <span class="wc-year">confidence {claim.confidence}</span>
        <ClaimLabel kind="assessment" />
        <SourceChip ids={claim.sources} />
      </div>
    </div>
  );
}

/** A claim the data dates after the day shown says nothing about that day. */
function NoReading({ label }: { label: string }) {
  return (
    <div class="wc-claim-card wc-claim-card--none">
      <div class="wc-claim-card-label">{label}</div>
      <div class="wc-claim-card-value">no reading yet</div>
      <div class="wc-claim-card-meta">
        <span class="wc-year">No source dates one on or before this day</span>
      </div>
    </div>
  );
}

function StatementColumn({ view, actors }: { view: WarView; actors: string[] }) {
  const { world } = useServices();
  const statements = view.statements.filter((s) => actors.includes(s.actor));
  const keys = uniqueKeys(statements.map(statementBase));
  const names = actors.map((a) => actorName(world, a)).join(' · ');
  return (
    <div class="wc-war-col">
      <div class="wc-war-col-head">{names}</div>
      {statements.length === 0 ? <div class="wc-empty">No sourced statements from this side yet</div> : null}
      {statements.map((s, i) => (
        <StatementCard key={keys[i]} s={s} />
      ))}
    </div>
  );
}

// ─── Theatre inset ───────────────────────────────────────────────────────────────────────

interface Crop {
  col0: number;
  row0: number;
  cols: number;
  rows: number;
}

/** Bounding box of the theatre states' hexes plus a margin, or null when it cannot be cropped. */
function theatreCrop(snap: WorldSnapshot, indices: Set<number>): Crop | null {
  let c0 = COLS;
  let c1 = -1;
  let r0 = Number.MAX_SAFE_INTEGER;
  let r1 = -1;
  for (let i = 0; i < HEX_COUNT; i++) {
    if (!indices.has(snap.owner[i])) {
      continue;
    }
    const c = hexCol(i);
    const r = hexRow(i);
    c0 = Math.min(c0, c);
    c1 = Math.max(c1, c);
    r0 = Math.min(r0, r);
    r1 = Math.max(r1, r);
  }
  if (c1 < 0 || c1 - c0 > INSET_MAX_COLS) {
    return null;
  }
  const rowsMax = HEX_COUNT / COLS - 1;
  const col0 = Math.max(0, c0 - INSET_MARGIN);
  const row0 = Math.max(0, r0 - INSET_MARGIN);
  return {
    col0,
    row0,
    cols: Math.min(COLS - 1, c1 + INSET_MARGIN) - col0 + 1,
    rows: Math.min(rowsMax, r1 + INSET_MARGIN) - row0 + 1,
  };
}

function paintInset(ctx: CanvasRenderingContext2D, snap: WorldSnapshot, crop: Crop, focus: Set<number>, world: WorldService): void {
  const w = crop.cols * INSET_PX_PER_COL + 1;
  const img = ctx.createImageData(w, crop.rows);
  const colours = new Map<number, { r: number; g: number; b: number }>();
  const colourOf = (actor: number) => {
    let c = colours.get(actor);
    if (!c) {
      c = parseHex(world.actor(actor)?.colors.primary ?? '#0d2b40');
      colours.set(actor, c);
    }
    return c;
  };

  for (let r = 0; r < crop.rows; r++) {
    const row = crop.row0 + r;
    for (let c = 0; c < crop.cols; c++) {
      const i = row * COLS + crop.col0 + c;
      const owner = snap.owner[i];
      const ctrl = snap.controller[i];
      let rgb = { r: 13, g: 43, b: 64 };
      let k = 1;
      if (owner !== 0) {
        rgb = colourOf(owner);
        k = focus.has(owner) ? 0.95 : INSET_DIM;
      }
      if (owner !== 0 && ctrl !== 0 && (c + row) % STRIPE_EVERY === 0) {
        rgb = colourOf(ctrl);
        k = 1;
      }
      const x0 = c * INSET_PX_PER_COL + (row & 1);
      for (let dx = 0; dx < INSET_PX_PER_COL; dx++) {
        const p = (r * w + x0 + dx) * 4;
        img.data[p] = rgb.r * k;
        img.data[p + 1] = rgb.g * k;
        img.data[p + 2] = rgb.b * k;
        img.data[p + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
}

function TheatreInset({ war }: { war: War }) {
  const { state, world } = useServices();
  const canvas = useRef<HTMLCanvasElement>(null);
  const snap = state.snapshot.value;

  // Theatre states as snapshot actor indices (countries keep their Country.id).
  const focus = new Set<number>();
  const sources: SourceId[] = [];
  for (const iso3 of war.theatre) {
    const r = reportOf(world, iso3);
    if (r) {
      focus.add(r.country.id);
      sources.push(...r.country.sources);
    }
  }
  const crop = snap ? theatreCrop(snap, focus) : null;
  const key = crop ? `${crop.col0}:${crop.row0}:${crop.cols}:${crop.rows}` : '';

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx || !snap || !crop) {
      return;
    }
    paintInset(ctx, snap, crop, focus, world);
  }, [snap, key]);

  if (!snap || !crop) {
    return <div class="wc-empty">No map inset for this theatre.</div>;
  }

  const held = [...new Set(war.theatre.flatMap((iso3) => controllersIn(snap, reportOf(world, iso3)?.country.id ?? -1)))];
  return (
    <figure class="wc-inset">
      <div class="wc-inset-map" style={{ aspectRatio: `${crop.cols * HEX_W} / ${crop.rows * ROW_H}` }}>
        <canvas ref={canvas} width={crop.cols * INSET_PX_PER_COL + 1} height={crop.rows} aria-label="Theatre map" />
      </div>
      <figcaption class="wc-inset-cap">
        <span>
          Borders and control at {fmtDate(snap.date)}
          {held.length > 0 ? ` · stripes: held by ${held.map((i) => world.actor(i)?.name ?? '?').join(', ')}` : ''}
        </span>
        <SourceChip ids={[...new Set(sources)]} />
      </figcaption>
    </figure>
  );
}

/** Actor indices that control hexes owned by `owner` in the snapshot. */
function controllersIn(snap: WorldSnapshot, owner: number): number[] {
  const out = new Set<number>();
  for (let i = 0; i < HEX_COUNT; i++) {
    if (snap.owner[i] === owner && snap.controller[i] !== 0) {
      out.add(snap.controller[i]);
    }
  }
  return [...out];
}

// ─── Control over time ───────────────────────────────────────────────────────────────────

function ControlChart({ war }: { war: War }) {
  const { world, state } = useServices();
  let series: ControlHistory | null = null;
  try {
    series = controlHistory(world, war.id);
  } catch {
    series = null;
  }
  if (!series || series.points.length < 2) {
    return null;
  }

  const color = lookOf(world, series.controller).colors.primary;
  const pts = series.points;
  const t0 = Date.parse(pts[0].date);
  const t1 = Date.parse(state.asOf.value);
  const span = t1 - t0 || 1;
  let max = 0;
  for (const p of pts) {
    max = Math.max(max, p.share);
  }
  const top = max > 0 ? max * CHART_HEADROOM : 1;
  const xy = pts.map((p) => ({ x: ((Date.parse(p.date) - t0) / span) * CHART_W, y: CHART_H - (p.share / top) * CHART_H }));
  const line = xy.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join('');
  const now = ((Date.parse(state.date.value) - t0) / span) * CHART_W;
  const last = pts[pts.length - 1];

  return (
    <div class="wc-ctl">
      <div class="wc-ctl-head">
        <span>
          Share of {lookOf(world, series.territoryOf).name} held by {lookOf(world, series.controller).name}
        </span>
        <ClaimLabel kind="assessment" />
        <SourceChip ids={series.sources} />
      </div>
      <div class="wc-ctl-plot">
        <span class="wc-ctl-y wc-num">
          <span>{fmtShare(max)}</span>
          <span>0%</span>
        </span>
        <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" aria-label="Control over time">
          <defs>
            <linearGradient id="wc-ctl-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stop-color={color} stop-opacity="0.55" />
              <stop offset="1" stop-color={color} stop-opacity="0.08" />
            </linearGradient>
          </defs>
          <line x1="0" x2={CHART_W} y1={CHART_H - (max / top) * CHART_H} y2={CHART_H - (max / top) * CHART_H} class="wc-ctl-grid" vector-effect="non-scaling-stroke" />
          <path d={`${line}L${xy[xy.length - 1].x.toFixed(1)} ${CHART_H}L0 ${CHART_H}Z`} fill="url(#wc-ctl-fill)" />
          <path d={line} fill="none" stroke={color} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
          {now >= 0 && now <= CHART_W ? <line x1={now} x2={now} y1="0" y2={CHART_H} class="wc-ctl-now" vector-effect="non-scaling-stroke" /> : null}
        </svg>
      </div>
      <div class="wc-ctl-axis">
        <span>{fmtDate(pts[0].date)}</span>
        <span class="wc-num">
          latest {fmtShare(last.share)} · {fmtDate(last.date)}
        </span>
      </div>
    </div>
  );
}
